import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { certificatesQuery } from '../api/softsee'
import { moduleOf, shellQuery } from '../api/shell'
import { CiFormModal } from '../cmdb/CiFormModal'
import { companyCell, dueCell, nameCell, propCell, type DomainColumnDef, type DomainRow } from '../overview/columns'
import { Content } from '../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'

/* Pastilele de expirare si randurile terse (`adm-row-down`, `adm-row-degraded`,
   `adm-nowrap`) sunt din adm.css - ecranul vechi o aducea prin `loadPlugins`.
   Bara de filtre a plecat in antet, culorile randurilor au ramas. */
const STYLES = ['css/adm.css']
const TABLE_ID = 'recTable'
const PAGE = 25

/**
 * Softsee::certificates - registrul CMDB, aceeasi macheta ca la domenii
 * (cmdb_records/index.php + records.js), si acum si acelasi tabel.
 *
 * PE `DataTable`, CA GEAMANUL LUI DE LA DOMENII
 * ---------------------------------------------------------------------------
 * Ecranul isi desena singur tabelul si paginarea, si tot singur isi punea
 * `tk-list-flush` pe <body>. Marginea paginii iesea CORECTA - masurata in Edge
 * fata de ecranul adevarat al portalului, 0/0/0/0 la 1600px si la 1280px, cap
 * la cap - dar in portal lipsa marginii e PLATITA de chromul listei: cautarea
 * urca in bara de sus, paginarea si Copy/Columns/Refresh coboara in subsol, iar
 * butonul verde se aseaza langa cautare. Aici nu venea nimic din toate astea,
 * deci ramanea o pagina lipita de margini cu un rand de paginare atarnand in
 * gol - "1-3 of 3" la x=0 si chenarul lui Next taiat de marginea ferestrei.
 * Asa arata un ecran caruia "ii lipseste spatierea", desi marginea era buna.
 *
 * `DataTable` aduce chromul intreg si pune si `tk-list-flush` - deci marginea
 * ramane exact cea de dinainte, si cea din portal. Se schimba numai ce e in
 * pagina, nu cat de lata e.
 *
 * DEASUPRA TABELULUI NU MAI STA NIMIC
 * ---------------------------------------------------------------------------
 * Si tot nu arata terminat. Ecranul isi tinea in pagina, deasupra tabelului, o
 * bara cu doua campuri - cautare si expirare - si sub ea randul care spune ce
 * e registrul. Masurat in Edge, la 1600px: eticheta primului camp la x=1,
 * randul de text la x=20, tabelul la x=0. Trei margini diferite pe aceeasi
 * coloana, de sus in jos - asta se citeste ca „nu are spatiere", si asta a
 * spus omul care se uita la produs, de doua ori.
 *
 * Prima incercare a fost sa li se dea TUTUROR marginea de 20px a paginii
 * (`gutter`, 11 septembrie 2026). Le alinia, dar tot cu bara si cu randul de
 * text deasupra tabelului - adica acoperea coloana zdrentuita in loc s-o
 * indrepte, si scotea ecranul din randul celorlalte liste.
 *
 * Raspunsul bun l-a dat tot ea: „search si expiry nu pot sta sus in header ca
 * sa facem tabelul full ca la celelalte si lipit sus?". Ba da:
 *
 *   - CAUTAREA nici macar nu trebuia mutata - `DataTable` deseneaza deja una in
 *     antet, legata de acelasi `state.search`. Erau DOUA casute de cautare pe
 *     ecran, una la x=536 in antet si una la x=20 in pagina, care faceau exact
 *     acelasi lucru. A ramas cea din antet, si a luat cu ea si indrumarea
 *     („common name, e.g. portal.example.ro") ca `searchHint`.
 *   - EXPIRAREA urca langa ea, cu `tk-lift-to-header tk-header-select` - locul
 *     pe care `list_chrome.css` il tine pentru alegerea proprie a unui ecran,
 *     acolo unde stau si selectul lui Discovered si cel al lui Applications
 *     Dashboard. Optiunile isi spun singure ce sunt („Any expiry"), fiindca in
 *     antet nu mai are cine sa le puna eticheta.
 *   - RANDUL DE TEXT nu mai are unde sta fara sa refaca zdreanta. Nu se pierde
 *     insa: se muta in starea goala a tabelului (`emptyHint`), acolo unde chiar
 *     e nevoie de el - un registru gol e singurul moment in care cineva se
 *     intreaba ce e si de unde incepe. Intr-un registru plin de certificate,
 *     acelasi paragraf e o propozitie citita o data si recitita de o suta de
 *     ori.
 *
 * Deasupra tabelului nu mai ramane nimic, deci nu mai e nimic de aliniat:
 * ecranul se intoarce lipit de margini, ca `/cmdb` si `/overview/domains`, si
 * `gutter` a fost scos cu totul - un intrerupator pe care nu-l mai apasa nimeni
 * e o intrebare pusa degeaba urmatorului care trece pe aici.
 *
 * Celulele se deseneaza cu functiile din `overview/columns.ts`: ele SUNT
 * `records.js` portat, iar scriptul ala slujeste amandoua registrele dintr-un
 * motiv scris chiar in capul lui - doua copii gemene se despart la a treia
 * schimbare. Si scapa textul: intr-un `html()` nu mai are cine sa o faca in
 * locul nostru, cum facea JSX-ul de dinainte.
 */
export function CertificatesPage() {
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const navigate = useNavigate()
  const [due, setDue] = useState('')
  const [adding, setAdding] = useState(false)
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: PAGE,
    order: { column: 0, dir: 'asc' },
    search: '',
  })

  /* Asteptarea de 400 ms dupa ultima tasta - `typingTimer` din `records.js`, ca
     "portal.cec.ro" sa nu plece litera cu litera - o tine acum `DataTable`
     insusi, pentru casuta lui din antet. Nu se mai scrie a doua oara aici. */

  const query = useQuery({
    ...certificatesQuery(state.search, due, state.start, state.length),
    placeholderData: keepPreviousData,
  })

  const page = query.data
  /* Coloanele din mijloc vin de la server (Registries::certificates), deci se
     construiesc din ce a trimis el, nu dintr-o lista scrisa a doua oara aici. */
  const columns = useMemo(
    () => certificateColumns(page?.columns ?? [], page?.dueLabel ?? 'Expires'),
    [page?.columns, page?.dueLabel],
  )

  if (query.isPending) {
    return (
      <Content>
        <Spinner />
      </Content>
    )
  }
  if (query.isError) {
    return (
      <Content>
        <div className="alert alert-danger">{(query.error as Error).message}</div>
      </Content>
    )
  }

  if (!page?.class) {
    return (
      <Content>
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <h4>
            <i className="fas fa-database"></i> {page?.title ?? 'Digital certificates'}
          </h4>
          <p>
            Registry not created yet. It lives in the CMDB as a class of its own, which every service provider gets
            automatically - when the provider is created, and from the ADM run every 15 minutes.
          </p>
          <p className="text-muted" style={{ marginBottom: 0 }}>
            If this message stays, run <code>php bin/console od:provider:defaults</code> on the server; it adds what is
            missing and this screen starts working right away.
          </p>
        </div>
      </Content>
    )
  }

  const canAdd = page.canEdit && page.classId > 0

  return (
    <Content>
      <DataTable
        id={TABLE_ID}
        columnTools
        columns={columns}
        rows={page.data as unknown as Record<string, unknown>[]}
        total={page.recordsFiltered}
        fetching={query.isFetching}
        error={null}
        onRetry={() => void query.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `rec_${row.ID}`)}
        /* Indrumarea de cautare a registrului (`Registries::certificates`), pe
           casuta din antet - acolo unde a ajuns cautarea. */
        searchHint={page.searchHint}
        /*
         * RANDUL CARE SPUNE CE E REGISTRUL, mutat aici din pagina.
         *
         * Deasupra tabelului nu mai sta nimic, si nici el nu se intoarce acolo:
         * era a treia margine pe aceeasi coloana. Dar nu se pierde - un registru
         * gol e exact momentul in care cineva se intreaba ce e si de unde
         * incepe, iar asta ii raspunde („Open one to fill in the rest"). Intr-un
         * registru plin, acelasi paragraf e o propozitie citita o data si
         * recitita de o suta de ori.
         */
        emptyHint={page.blurb}
        /*
         * EXPIRAREA, in bara de sus, langa cautare.
         *
         * `tk-lift-to-header tk-header-select` e locul pe care `list_chrome`
         * il tine pentru alegerea proprie a unui ecran - acolo stau si selectul
         * lui Discovered ("ce arata lista"), si cel al lui Applications
         * Dashboard. In portal clasa o pune `list_chrome.js`; aici nu exista
         * list_chrome.js, deci o scrie pagina.
         *
         * Optiunile isi spun singure ce sunt: in antet nu mai are cine sa le
         * puna eticheta „Expiry", iar un select pe care scrie doar „Any" nu
         * spune nimanui la ce se uita.
         *
         * Si sunt SCURTE, fiindca un select se largeste cat cea mai lunga
         * optiune a lui, iar antetul e singurul loc din pagina cu latimea
         * numarata: „Expiring in 30 days" il facea de 143px, si pe un ecran de
         * 1280 impingea butonul verde peste panoul din dreapta. „30d" e chiar
         * scrisul pastilelor din coloana de expirare („52d left"), deci nu e o
         * prescurtare inventata aici.
         */
        headerView={
          <div className="tk-lift-to-header tk-header-select" style={{ display: 'inline-block', textAlign: 'left' }}>
            <select
              className="form-control input-sm"
              id="recDue"
              title="Expiry"
              aria-label="Expiry"
              value={due}
              onChange={(e) => {
                setDue(e.target.value)
                setState({ start: 0 })
              }}
            >
              <option value="">Any expiry</option>
              <option value="expired">Expired</option>
              <option value="soon">Expiring in 30d</option>
            </select>
          </div>
        }
        /*
         * "Add certificate" in BARA DE SUS, langa cautare, ca la celelalte
         * liste portate (vezi SNMP Hosts si SNMP Services, 11 septembrie 2026).
         * In portal e un buton DataTables scris in `records.js`, pe care
         * `list_chrome.js` il urca acolo cu clasa `tk-header-extra`.
         */
        headerButtons={
          canAdd ? (
            <button type="button" className="btn btn-success tk-header-extra" onClick={() => setAdding(true)}>
              <i className="fas fa-plus" /> Add certificate
            </button>
          ) : undefined
        }
        /* Copy / Columns / Refresh - `DataTablesProperties.buttons` din macheta
           veche, pe care list_chrome le coboara in subsol. */
        footerButtons={{ copy: true, colvis: true, refresh: () => void query.refetch() }}
      >
        {adding ? (
          <CiFormModal
            id={0}
            classId={page.classId}
            onClose={() => setAdding(false)}
            onSaved={(id) => {
              setAdding(false)
              /*
               * Dus pe fisa lui, nu inapoi in lista - ca in `records.js`:
               * certificatul tocmai facut n-are inca nicio proprietate scrisa,
               * nici data de expirare, nici emitentul, si acolo se completeaza.
               * Lasat in lista, ar fi ramas un rand gol pe care sa-l caute.
               */
              if (id > 0) void navigate({ to: '/cmdb/view/$id', params: { id: String(id) } })
              else void query.refetch()
            }}
          />
        ) : null}
      </DataTable>
    </Content>
  )
}

/** Coloanele registrului: Name, Customer, proprietatile clasei, si expirarea. */
function certificateColumns(
  defs: { key: string; label: string; type?: string }[],
  dueLabel: string,
): TableColumn[] {
  /* `ordering: false` in `records.js`: ordinea o hotaraste serverul (ce expira
     intai), iar un antet pe care se apasa fara sa se schimbe nimic e mai rau
     decat unul pe care nu se apasa. */
  const cols: TableColumn[] = [
    {
      index: 0,
      title: 'Name',
      orderable: false,
      visible: true,
      html: (row) => nameCell(row as unknown as DomainRow, '/cmdb'),
    },
    {
      index: 1,
      title: 'Customer',
      orderable: false,
      visible: true,
      html: (row) => companyCell((row as unknown as DomainRow).company_name),
    },
  ]
  defs.forEach((def, i) => {
    const column: DomainColumnDef = {
      key: def.key,
      label: def.label,
      type: (def.type as DomainColumnDef['type']) ?? 'text',
    }
    cols.push({
      index: 2 + i,
      title: def.label,
      orderable: false,
      visible: true,
      html: (row) => propCell(row as unknown as DomainRow, column),
    })
  })
  cols.push({
    index: 2 + defs.length,
    title: dueLabel,
    orderable: false,
    visible: true,
    /* `adm-nowrap`, ca in `records.js`: "2026-11-02 - 52d left" nu se rupe pe
       doua randuri intr-o coloana ingusta. */
    className: 'adm-nowrap',
    html: (row) => dueCell(row as unknown as DomainRow),
    /* Semaforul pe RANDUL intreg, nu doar pe pastila - `createdRow` din
       `records.js`: un registru de o suta de certificate se scaneaza cu ochiul. */
    rowClass: (row) => {
      const state = (row as unknown as DomainRow).due_state
      if (state === 'expired') return 'adm-row-down'
      if (state === 'soon') return 'adm-row-degraded'
      return undefined
    },
  })
  return cols
}
